import React, { useState, useMemo } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcBinGrpRelationService, StcBinGrpRelationDto, CreateStcBinGrpRelationRequest } from '../../../../../services/stcBinGrpRelationService';
import { StcBinGrpDto } from '../../../../../services/stcBinGrpService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface BinGrpRelationCreateModalProps {
    isOpen: boolean;
    binGroups: StcBinGrpDto[];
    existingRelations: StcBinGrpRelationDto[];
    onClose: () => void;
    onSuccess: () => void;
}

export const BinGrpRelationCreateModal: React.FC<BinGrpRelationCreateModalProps> = ({
    isOpen,
    binGroups,
    existingRelations,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [masterBinGrpNo, setMasterBinGrpNo] = useState<number>(0);
    const [childBinGrpNo, setChildBinGrpNo] = useState<number>(0);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    // Kun fellesgrupper kan være master
    const masterCandidates = binGroups.filter(g => g.commonAvailable);

    // Grupper som allerede er master — kan ikke velges som sub
    const allMasterNos = useMemo(
        () => existingRelations.map(r => r.masterBinGrpNo).filter((v, i, arr) => arr.indexOf(v) === i),
        [existingRelations]
    );

    // Tilgjengelige barn-grupper (ekskluder grupper som er master)
    const childCandidates = binGroups.filter(g => !allMasterNos.includes(g.binGrpNo));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (masterBinGrpNo === 0 || childBinGrpNo === 0) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.selectBoth', {}, 'Velg både master-gruppe og barn-gruppe'));
            return;
        }
        if (masterBinGrpNo === childBinGrpNo) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.sameGroup', {}, 'Master og barn kan ikke være samme gruppe'));
            return;
        }
        // Dobbeltsjekk: master må være fellesgruppe
        const masterGrp = binGroups.find(g => g.binGrpNo === masterBinGrpNo);
        if (masterGrp && !masterGrp.commonAvailable) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.masterMustBeCommon', {}, 'Master-gruppen må være merket som Fellesgruppe (commonAvailable). Endre innstillingen på lokasjonsgruppen først.'));
            return;
        }
        // Barn-gruppe kan ikke være en eksisterende master
        if (allMasterNos.includes(childBinGrpNo)) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.childIsMaster', {}, 'Valgt barn-gruppe er allerede registrert som Master og kan ikke være Sub.'));
            return;
        }

        const request: CreateStcBinGrpRelationRequest = {
            companyNo: user?.companyNo || '',
            masterBinGrpNo,
            childBinGrpNo
        };

        try {
            setIsSaving(true);
            setError('');
            await stcBinGrpRelationService.create(request);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminBins.binGrpRelation.errors.createFailed', {}, 'Feil ved opprettelse av relasjon'));
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div
                    className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminBins.binGrpRelation.create.title', {}, 'Ny lokasjonsgrupperelasjon')}</h2>
                            <p className="text-sm text-green-100">{t('manager.modules.adminBins.binGrpRelation.create.subtitle', {}, 'Opprett ny relasjon mellom lokasjonsgrupper')}</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">{t('manager.modules.adminBins.binGrpRelation.create.success', {}, 'Relasjon opprettet!')}</p>
                                </div>
                            </div>
                        )}

                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminBins.binGrpRelation.relationLegend', {}, 'Relasjon')}</legend>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminBins.binGrpRelation.fields.masterGroup', {}, 'Master-gruppe')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={masterBinGrpNo}
                                        onChange={(e) => setMasterBinGrpNo(parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                        required
                                        autoFocus
                                    >
                                        <option value={0}>{t('manager.modules.adminBins.binGrpRelation.selectMaster', {}, 'Velg master-gruppe...')}</option>
                                        {masterCandidates.map(group => (
                                            <option key={group.id} value={group.binGrpNo}>
                                                {group.binGrpNo} - {group.description1}
                                            </option>
                                        ))}
                                    </select>
                                    {masterCandidates.length === 0 ? (
                                        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700">
                                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                            {t('manager.modules.adminBins.binGrpRelation.noCommonGroups', {}, 'Ingen lokasjonsgrupper er merket som Fellesgruppe. Sett «Felles tilgjengelig» på minst én gruppe under Lokasjonsgrupper først.')}
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-xs text-gray-400">
                                            {t('manager.modules.adminBins.binGrpRelation.onlyCommonAsMaster', {}, 'Kun grupper merket som Fellesgruppe kan være master.')}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminBins.binGrpRelation.fields.childGroup', {}, 'Barn-gruppe')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={childBinGrpNo}
                                        onChange={(e) => setChildBinGrpNo(parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                        required
                                    >
                                        <option value={0}>{t('manager.modules.adminBins.binGrpRelation.selectChild', {}, 'Velg barn-gruppe...')}</option>
                                        {childCandidates.map(group => (
                                            <option key={group.id} value={group.binGrpNo}>
                                                {group.binGrpNo} - {group.description1}
                                            </option>
                                        ))}
                                    </select>
                                    {allMasterNos.length > 0 && (
                                        <p className="mt-1 text-xs text-gray-400">
                                            {t('manager.modules.adminBins.binGrpRelation.masterCannotBeChild', {}, 'Grupper som er registrert som Master kan ikke velges som barn.')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminBins.common.creating', {}, 'Oppretter...')}
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {t('manager.modules.adminBins.binGrpRelation.create.submit', {}, 'Opprett relasjon')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
